import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, User, Layout, Sparkles, ArrowUpRight } from 'lucide-react';

const services = [
  {
    id: 'business',
    title: 'Business Website',
    description: 'Professional online presence with lead generation, CRM integration, and analytics dashboard.',
    icon: Briefcase,
    image: 'https://images.unsplash.com/photo-1664355680555-12687f77e0d3?w=400&q=80',
    features: ['Lead Capture Forms', 'Analytics Integration', 'CRM Connection'],
  },
  {
    id: 'personal',
    title: 'Personal Website',
    description: 'Showcase your portfolio, blog, or personal brand with stunning design and performance.',
    icon: User,
    image: 'https://images.unsplash.com/photo-1635397990741-81cb230b154b?w=400&q=80',
    features: ['Portfolio Gallery', 'Blog Integration', 'Social Links'],
  },
  {
    id: 'landing',
    title: 'Landing Page',
    description: 'High-converting single-page experiences optimized for campaigns and product launches.',
    icon: Layout,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&q=80',
    features: ['A/B Testing Ready', 'Fast Load Times', 'Conversion Focused'],
  },
  {
    id: 'custom',
    title: 'Custom Website',
    description: 'Tailored solutions for unique requirements, complex features, and enterprise needs.',
    icon: Sparkles,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80',
    features: ['Custom Features', 'API Integrations', 'Scalable Architecture'],
  },
];

const Services = () => {
  return (
    <section 
      id="services" 
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #050508 0%, #0a0a12 50%, #050508 100%)',
      }}
      data-testid="services-section"
    >
      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#7B2EFF]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#00D4FF]/5 rounded-full blur-[120px]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] text-[#7B2EFF] bg-[#7B2EFF]/10 border border-[#7B2EFF]/20 backdrop-blur-sm mb-4">
            Our Services
          </span>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-white">Solutions for Every Need</h2>
          <p className="mt-4 text-white/50 max-w-2xl mx-auto text-lg">
            From simple landing pages to complex web applications, we deliver pixel-perfect experiences.
          </p>
        </motion.div>

        {/* Services Grid - Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative rounded-3xl overflow-hidden glass-card"
              data-testid={`service-card-${service.id}`}
            >
              {/* Image with overlay */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a12] via-[#0a0a12]/70 to-transparent" />
                
                {/* Floating icon badge */}
                <motion.div 
                  className="absolute top-4 left-4 w-14 h-14 rounded-2xl grid place-items-center glass-card"
                  whileHover={{ scale: 1.1 }}
                >
                  <service.icon className="w-6 h-6 text-[#7B2EFF]" />
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                  {service.title}
                  <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1 group-hover:-translate-y-1 text-[#7B2EFF]" />
                </h3>
                <p className="text-white/50 text-sm mb-4 leading-relaxed">{service.description}</p>

                {/* Features as glass pills */}
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 rounded-full text-xs text-white/70 bg-white/5 border border-white/10 backdrop-blur-sm"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {/* Hover glow effect */}
              <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle at 50% 0%, rgba(123, 46, 255, 0.1) 0%, transparent 60%)',
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
